/**
 * JsonHeaderGlyph — clickable JSON-state glyph rendered in a result-grid
 * column header. Three states: declared (blue {}), native (gray {}),
 * declarable (gray {}?). Presentational only — the parent passes a resolved
 * state and an onClick(columnId) wrapper. Shared by TableExplorerGrid and
 * the Query Editor (useCellRenderer). Testids preserve the pre-extraction
 * values so existing JSON e2e specs stay green.
 */

import React from 'react';

import { GLYPH_DECLARED_CLASS, GLYPH_MUTED_CLASS } from './gridGlyphStyles';

export type JsonGlyphState = 'declared' | 'native' | 'declarable';

type JsonHeaderGlyphProps = {
  state: JsonGlyphState;
  onClick: () => void;
};

const CONFIG: Record<
  JsonGlyphState,
  { testId: string; label: string; title: string; className: string }
> = {
  declared: {
    testId: 'grid-header-json-prefix',
    label: '{}',
    title: 'Declared as JSON — click for details',
    className: GLYPH_DECLARED_CLASS,
  },
  native: {
    testId: 'grid-header-json-native',
    label: '{}',
    title: 'Native JSON column — click to configure',
    className: GLYPH_MUTED_CLASS,
  },
  declarable: {
    testId: 'grid-header-json-declarable',
    label: '{}?',
    title: 'Looks like JSON — click to declare',
    className: GLYPH_MUTED_CLASS,
  },
};

export const JsonHeaderGlyph = React.memo(function JsonHeaderGlyph({
  state,
  onClick,
}: JsonHeaderGlyphProps) {
  const c = CONFIG[state];
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={c.className}
      title={c.title}
      aria-label={c.title}
      data-testid={c.testId}
    >
      {c.label}
    </button>
  );
});
